import React, { useEffect, useState } from "react";
import { Button, Table, Checkbox, Row, Col, Tooltip, Typography } from "antd";
import moment from 'moment';
import { SchemaModelFieldHistoryList, SchemaModelChangedAttributes, LoadingOverlay, FullHeightContainerLayout } from "components";
import _ from 'lodash';
import CypressTestIds from "../../../cypress/CypressTestIds";

const { Column } = Table;
const { Title, Text, Link } = Typography;

const RecordDetail = ({ recordType, recordId, record, ...props }) => {
    switch (recordType) {
        case "SCHEMA_MODEL":
            return <SchemaModelChangedAttributes key={recordId} record={record}></SchemaModelChangedAttributes>;
        case "SCHEMA_MODEL_FIELDS":
            return (
                <>
                    {
                        record.loading ?
                            <LoadingOverlay
                                busy={true}
                                spinner
                                message="Getting schema fields...">
                            </LoadingOverlay>
                            :
                            <FullHeightContainerLayout
                                showHeader={true}
                                showFooter={false}
                                header={<Title level={5}><Text>Schema Fields</Text></Title>}
                                content={<SchemaModelFieldHistoryList key={recordId} schemaModelFieldRevisions={record.data}></SchemaModelFieldHistoryList>}>
                            </FullHeightContainerLayout>
                    }
                </>
            )
            return <></>
        default:
            return <></>
    }
}

const SchemaModelHistoryList = ({ schemaModelRevisions, selectRecordDetail = null, onLoadSchemaModelFieldRevisions }) => {
    const [recordDetail, setRecordDetail] = useState(null);
    const [containerHeight, setContainerHeight] = useState("100%");
    useEffect(() => {
        setRecordDetail(selectRecordDetail);
    }, [selectRecordDetail]);
    return (
        <Row style={{ flexDirection: "column", flexGrow: 1, height: "100%" }}>
            <Col span={24} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <Row style={{ flexDirection: "column", flexGrow: 1, height: containerHeight }}>
                    <Col span={24}>
                        <FullHeightContainerLayout
                            content={<Table size="small"
                                rowKey="metadata.txId"
                                dataSource={schemaModelRevisions}
                                pagination={false}
                                scroll={{ y: `100vh` }}
                                className={"container-height-100"}
                                data-testid={CypressTestIds.COMMON_OBJECTS_REVISIONS_LIST_TABLE}>
                                <Column
                                    width="3rem"
                                    title="No."
                                    key="index"
                                    render={(value, revision, index) => index + 1}
                                />
                                <Column
                                    width="12rem"
                                    dataIndex={["data", "businessAreaName"]}
                                    title={"Business Area"}
                                    render={(value, revision, index) => <>{value || "Global"}</>}
                                />
                                <Column
                                    width="10rem"
                                    dataIndex={["data", "name"]}
                                    title={"Name"}
                                    render={(value, revision, index) => <>{value}</>}
                                />
                                <Column
                                    width="12rem"
                                    dataIndex={["data", "description"]}
                                    title={"Description"}
                                    ellipsis={{
                                        showTitle: false,
                                    }}
                                    render={(value, revision, index) => <Tooltip placement="topLeft" title={value}>{value}</Tooltip>}
                                />
                                <Column
                                    width="12rem"
                                    dataIndex={["data", "state"]}
                                    title={"State"}
                                    ellipsis={{
                                        showTitle: false,
                                    }}
                                    render={(value, revision, index) => <Tooltip placement="topLeft" title={value}>{value}</Tooltip>}
                                />
                                <Column
                                    width="10rem"
                                    dataIndex={["data", "fieldsCount"]}
                                    title={"fields"}
                                    ellipsis={{
                                        showTitle: false,
                                    }}
                                    render={(value, revision, index) => <Tooltip placement="topLeft" title={value}>
                                        {onLoadSchemaModelFieldRevisions || (revision.data.fieldResult && revision.data.fieldResult.Items && revision.data.fieldResult.Items.length > 0)
                                            ?
                                            <Link onClick={() => {
                                                setContainerHeight("50%");
                                                if (revision.data.fieldResult && revision.data.fieldResult.Items && revision.data.fieldResult.Items.length > 0) {
                                                    setRecordDetail({ type: "SCHEMA_MODEL_FIELDS", recordId: revision.data.schemaId, data: revision.data.fieldResult.Items });
                                                }
                                                else if (onLoadSchemaModelFieldRevisions) {
                                                    onLoadSchemaModelFieldRevisions(revision.data.businessAreaId, revision.data.schemaId, revision.metadata.version);
                                                    setRecordDetail({ type: "SCHEMA_MODEL_FIELDS", recordId: revision.data.schemaId, loading: true });
                                                }
                                            }}>
                                                {value}
                                            </Link>
                                            :
                                            <>{value}</>}
                                    </Tooltip>}
                                />
                                <Column
                                    width="15rem"
                                    dataIndex={["data", "createdByEmail"]}
                                    title={"Created By"}
                                    ellipsis={{
                                        showTitle: false,
                                    }}
                                    render={(value, revision, index) => <Tooltip placement="topLeft" title={value}>{value}</Tooltip>}
                                />
                                <Column
                                    width="12rem"
                                    dataIndex={["data", "createdDate"]}
                                    title={"Created Date"}
                                    render={(value, revision, index) => <>{moment.utc(value).toDate().toLocaleString()}</>}
                                />
                                <Column
                                    width="15rem"
                                    dataIndex={["data", "updatedByEmail"]}
                                    title={"Updated By"}
                                    ellipsis={{
                                        showTitle: false,
                                    }}
                                    render={(value, revision, index) => <Tooltip placement="topLeft" title={value}>{value}</Tooltip>}
                                />
                                <Column
                                    width="12rem"
                                    dataIndex={["data", "updatedDate"]}
                                    title={"Updated Date"}
                                    render={(value, revision, index) => <>{moment.utc(value).toDate().toLocaleString()}</>}
                                />
                                <Column
                                    width="12rem"
                                    dataIndex={"changes"}
                                    title={"Changes"}
                                    render={(value, revision, index) => {
                                        if (value) {
                                            return <>
                                                {Object.keys(value).reduce((items, key) => {
                                                    if (Object.keys(value[key]).length > 0) {
                                                        items.push(<Row key={key}>
                                                            <Col>
                                                                <Link onClick={() => {
                                                                    setContainerHeight("50%");
                                                                    setRecordDetail({
                                                                        type: "SCHEMA_MODEL",
                                                                        recordId: revision.data.schemaId,
                                                                        name: `${_.capitalize(key)} attributes`,
                                                                        data: value[key],
                                                                        previousData: revision.previousChanges && revision.previousChanges[key]
                                                                    });
                                                                }}>
                                                                    {`${key} attributes`}
                                                                </Link>
                                                            </Col>
                                                        </Row>);
                                                    }
                                                    return items;
                                                }, [])}
                                            </>
                                        }
                                        return <></>;
                                    }}
                                />
                            </Table>}>
                        </FullHeightContainerLayout>
                    </Col>
                </Row>
                {
                    recordDetail ?
                        <Row style={{ flexDirection: "column", flexGrow: 1, height: containerHeight }}>
                            <Col span={24}>
                                <FullHeightContainerLayout
                                    showHeader={false}
                                    content={<RecordDetail recordType={recordDetail.type} recordId={recordDetail.recordId} record={recordDetail}></RecordDetail>}
                                    showFooter={false}>
                                </FullHeightContainerLayout>
                            </Col>
                        </Row>
                        :
                        <></>
                }
            </Col>
        </Row>
    );
};

export default SchemaModelHistoryList;